/* eslint-disable no-undef */
const STORAGE_KEY = 'sxmsir_user_contact'
const DATES_STORAGE_KEY = 'sxmsir_user_dates'

export const getUserContact = () => {
  if (typeof window === 'undefined') return {}
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch (error) {
    // Only log in development
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.error('[getUserContact] Storage error:', error)
    }
    return {}
  }
}

/**
 * Sanitize string input to prevent XSS
 * Removes HTML tags, script content, and event handlers
 * Defense-in-depth: sessionStorage data is not rendered as HTML but sanitize to prevent any misuse
 */
const sanitizeString = (value) => {
  if (typeof value !== 'string') return value

  // Remove script tags and their content
  let sanitized = value.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    ''
  )

  // Remove all HTML tags (including malformed ones)
  sanitized = sanitized.replace(/<[^>]*>/g, '')

  // Remove event handler attributes that might be in text (onerror=, onclick=, etc.)
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')

  // Remove javascript: protocol handlers
  sanitized = sanitized.replace(/javascript:/gi, '')

  // Remove data: URIs that could contain scripts
  sanitized = sanitized.replace(/data:text\/html[^,]*,/gi, '')

  return sanitized.trim()
}

/**
 * Validate and sanitize contact data before storage
 * Only allows whitelisted keys with appropriate types and field-specific length limits
 */
const validateContactData = (data) => {
  const allowedKeys = ['name', 'email', 'phone', 'message', 'preferredContact']

  // Field-specific maximum lengths to prevent DOS and ensure data quality
  const maxLengths = {
    name: 100, // Reasonable for full names
    email: 254, // RFC 5321 email address maximum
    phone: 20, // International phone numbers with formatting
    message: 500, // Sufficient for contact messages
    preferredContact: 50 // Enum-like value
  }

  const validated = {}

  Object.keys(data).forEach((key) => {
    // Only allow whitelisted keys
    if (!allowedKeys.includes(key)) return

    const value = data[key]

    // Validate and sanitize based on type
    if (typeof value === 'string') {
      const sanitized = sanitizeString(value)
      const maxLength = maxLengths[key] || 100

      // Only store non-empty strings within length limit
      if (sanitized.length > 0 && sanitized.length <= maxLength) {
        validated[key] = sanitized
      }
    }
  })

  return validated
}

export const saveUserContact = (data) => {
  if (typeof window === 'undefined') return
  try {
    const existing = getUserContact()
    const validatedData = validateContactData(data)
    const updated = { ...existing, ...validatedData }

    // Only save non-empty values
    Object.keys(updated).forEach((key) => {
      if (!updated[key]) delete updated[key]
    })

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  } catch (error) {
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.error('[saveUserContact] Storage error:', error)
    }
  }
}

export const clearUserContact = () => {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.error('[clearUserContact] Storage error:', error)
    }
  }
}

export const getUserDates = () => {
  if (typeof window === 'undefined') return null
  try {
    const stored = sessionStorage.getItem(DATES_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.error('[getUserDates] Storage error:', error)
    }
    return null
  }
}

export const saveUserDates = (dates) => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    // Validate that dates are moment objects or have toISOString method before serializing
    const data = {
      startDate:
        dates.startDate && typeof dates.startDate.toISOString === 'function'
          ? dates.startDate.toISOString()
          : null,
      endDate:
        dates.endDate && typeof dates.endDate.toISOString === 'function'
          ? dates.endDate.toISOString()
          : null,
      offsetable: dates.offsetable !== undefined ? dates.offsetable : 0
    }
    sessionStorage.setItem(DATES_STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.error('[saveUserDates] Error saving to sessionStorage', error)
    }
  }
}

export const clearUserDates = () => {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(DATES_STORAGE_KEY)
  } catch (error) {
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.error('[clearUserDates] Storage error:', error)
    }
  }
}
